import { useEffect, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Gift, Mail, MessageCircle, Share2 } from "lucide-react";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/referral").then((r) => setData(r.data)).catch((e) => toast.error(getApiError(e)));
  }, []);

  if (!data) return null;
  const link = `${window.location.origin}/register?ref=${data.referral_code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console -- clipboard API can fail in restricted contexts (iframe, http)
      console.warn("Clipboard copy failed", err);
      toast.error("Couldn't copy");
    }
  };

  const message = `I'm in the Azure Gypsy Guild — they helped relieve my mortgage in 60 days through a counterclaim. Join with my link and we both get $250 off our yearly membership: ${link}`;

  const smsHref = `sms:?&body=${encodeURIComponent(message)}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("Azure Gypsy Guild — $250 off")}&body=${encodeURIComponent(message)}`;

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Azure Gypsy Guild", text: message, url: link }); } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const credits = data.credits_usd || 0;

  return (
    <Card className="border-[color:var(--bright-green)]/30 bg-gradient-to-br from-[color:var(--bright-green)]/8 to-transparent" data-testid="referral-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-serif text-2xl flex items-center gap-2">
            <Gift className="h-5 w-5 text-[color:var(--bright-green)]" />
            Refer & earn $250
          </CardTitle>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">Your credits</div>
            <div className="font-serif text-2xl text-[color:var(--bright-green)]" data-testid="referral-credits">${credits.toLocaleString()}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[color:var(--text-secondary)]">
          Refer a homeowner. When they join the Guild with your link, <strong>you get $250 off next year's membership</strong>.
          {data.referral_count > 0 && <> So far <strong>{data.referral_count}</strong> {data.referral_count === 1 ? "person has" : "people have"} joined via your link.</>}
        </p>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input readOnly value={link} className="font-mono text-xs" data-testid="referral-link-input" />
          <Button onClick={copy} className="bg-[color:var(--bright-green)] hover:bg-[color:var(--bright-green-hover)] text-white whitespace-nowrap" data-testid="referral-copy-btn">
            <Copy className="h-4 w-4 mr-2" />{copied ? "Copied" : "Copy link"}
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button onClick={share} variant="outline" className="border-black/10" data-testid="referral-share-btn">
            <Share2 className="h-4 w-4 mr-2" />Share
          </Button>
          <a href={smsHref}><Button variant="outline" className="w-full border-black/10" data-testid="referral-sms-btn"><MessageCircle className="h-4 w-4 mr-2" />SMS</Button></a>
          <a href={waHref} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="w-full border-black/10" data-testid="referral-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</Button></a>
          <a href={mailHref}><Button variant="outline" className="w-full border-black/10" data-testid="referral-email-btn"><Mail className="h-4 w-4 mr-2" />Email</Button></a>
        </div>

        <p className="text-xs text-[color:var(--text-secondary)] mt-4">
          Your code: <code className="bg-[color:var(--bg-secondary)] px-1.5 py-0.5 rounded text-[color:var(--fire-red)]" data-testid="referral-code">{data.referral_code}</code>
        </p>
      </CardContent>
    </Card>
  );
}
